import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import icone from "@/assets/icone.png";
import { Calculator, ChevronDown, Wrench, Menu } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useState } from "react";

interface HeaderProps {
  showAuth?: boolean;
}

const navItems = [
  { label: "Início", to: "/", type: "link" as const },
  { label: "Estruturação", to: "/consultoria", type: "link" as const },
  { label: "Planos", to: "/#pricing", type: "anchor" as const },
  { label: "Depoimentos", to: "/#testimonials", type: "anchor" as const },
  { label: "Blog", to: "/blog", type: "link" as const },
];

const Header = ({ showAuth = true }: HeaderProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b border-border" role="banner">
      <nav className="container mx-auto px-4 py-3" aria-label="Navegação principal">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 group min-w-0" aria-label="Voltar para página inicial">
            <img src={icone} alt="Bianchim Business" className="h-10 w-auto group-hover:scale-105 transition-transform" />
            <span className="text-lg font-bold text-foreground hidden sm:block truncate">
              Bianchim Business
            </span>
          </Link>

          {/* Links inline — apenas em desktop ≥1024px */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) =>
              item.type === "link" ? (
                <Link key={item.label} to={item.to} className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                  {item.label}
                </Link>
              ) : (
                <a key={item.label} href={item.to} className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                  {item.label}
                </a>
              ),
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 border-primary/30 text-primary hover:bg-primary/10 font-semibold">
                  <Wrench className="w-4 h-4" />
                  <span className="hidden sm:inline">Ferramentas</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/calculadora-roi")} className="cursor-pointer gap-2">
                  <Calculator className="w-4 h-4 text-primary" />
                  <div>
                    <p className="font-medium">Calculadora de ROI</p>
                    <p className="text-xs text-muted-foreground">Calcule o retorno do treinamento</p>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {showAuth && (
              <Button size="sm" onClick={() => navigate("/auth")} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-glow">
                Entrar
              </Button>
            )}

            {/* Hamburger — apenas tablet (md a lg) */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="inline-flex lg:hidden border-primary/30 text-primary hover:bg-primary/10"
                  aria-label="Abrir menu de navegação"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle>Navegação</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-1 mt-6">
                  {navItems.map((item) =>
                    item.type === "link" ? (
                      <Link
                        key={item.label}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className="px-3 py-3 rounded-lg text-base font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <a
                        key={item.label}
                        href={item.to}
                        onClick={() => setOpen(false)}
                        className="px-3 py-3 rounded-lg text-base font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                      >
                        {item.label}
                      </a>
                    ),
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;

